import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  LayoutDashboard, Map, Plus, List, Settings, LogOut,
  Menu, X, MapPin, Bell, User, ChevronDown, Users, Shield,
  Wifi, WifiOff, RefreshCw
} from 'lucide-react';
import { countPending, syncPending } from '../lib/offlineQueue';
import { toast } from 'sonner';

const navItems = {
  brigadista: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Nuevo Reporte', icon: Plus },
    { path: '/mis-gestiones', label: 'Mis Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
  ],
  gestion: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/notificaciones', label: 'Notificaciones', icon: Bell },
  ],
  funcionario: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Nuevo Reporte', icon: Plus },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/equipo', label: 'Equipo', icon: Users },
    { path: '/secciones', label: 'Secciones', icon: Settings },
    { path: '/notificaciones', label: 'Notificaciones', icon: Bell },
  ],
  admin: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Nuevo Reporte', icon: Plus },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/funcionarios', label: 'Funcionarios', icon: Shield },
    { path: '/secciones', label: 'Secciones', icon: Settings },
    { path: '/notificaciones', label: 'Notificaciones', icon: Bell },
  ],
};

const mobileBottomNav = {
  brigadista: [
    { path: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Reportar', icon: Plus },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
  ],
  gestion: [
    { path: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/notificaciones', label: 'Avisos', icon: Bell },
  ],
  funcionario: [
    { path: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Reportar', icon: Plus },
    { path: '/mis-gestiones', label: 'Gestiones', icon: List },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/equipo', label: 'Equipo', icon: Users },
  ],
  admin: [
    { path: '/dashboard', label: 'Inicio', icon: LayoutDashboard },
    { path: '/nueva-gestion', label: 'Reportar', icon: Plus },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/funcionarios', label: 'Funcionarios', icon: Shield },
  ],
};

const roleLabels = {
  brigadista: 'Brigadista',
  gestion: 'Equipo de Gestion',
  funcionario: 'Funcionario',
  admin: 'Administrador',
};

export default function Layout({ children }) {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    try { setPending(await countPending()); } catch { /* db not ready */ }
  }, []);

  useEffect(() => { refreshPending(); }, [refreshPending, location.pathname]);

  const doSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const apiUpload = (path, fd) => api.post(path, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await syncPending((p, b) => api.post(p, b), apiUpload);
      if (res.succeeded > 0) toast.success(`${res.succeeded} gestión(es) sincronizada(s)`);
      if (res.failed > 0) toast.warning(`${res.failed} pendiente(s) sin sincronizar`);
    } catch (e) {
      console.error(e);
    } finally {
      setSyncing(false);
      refreshPending();
    }
  }, [api, syncing, refreshPending]);

  useEffect(() => {
    const onOn = () => { setOnline(true); doSync(); };
    const onOff = () => setOnline(false);
    window.addEventListener('online', onOn);
    window.addEventListener('offline', onOff);
    // Auto-sync every 30s if there are pending
    const interval = setInterval(() => { if (navigator.onLine) refreshPending().then(() => { if (pending > 0) doSync(); }); }, 30000);
    return () => {
      window.removeEventListener('online', onOn);
      window.removeEventListener('offline', onOff);
      clearInterval(interval);
    };
  }, [doSync, refreshPending, pending]);

  const items = navItems[user?.role] || navItems.brigadista;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="app-layout">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm safe-top">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between safe-x">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 -ml-2 rounded-md hover:bg-[#FAFAFA] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2.5">
              <img src="/logo.png" alt="Ciudad Inteligente" className="w-8 h-8 rounded-md object-contain" />
              <span className="text-lg font-black tracking-tighter text-[#0A0A0A] hidden sm:block" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Ciudad Inteligente
              </span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {items.map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'bg-[#0052FF] text-white'
                      : 'text-[#52525B] hover:bg-[#10B981] hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu + Sync Status */}
          <div className="flex items-center gap-2">
            {(!online || pending > 0) && (
              <button
                data-testid="sync-status-btn"
                onClick={doSync}
                disabled={!online || syncing}
                className={`h-9 px-2.5 rounded-md flex items-center gap-1.5 text-xs font-bold transition-all ${
                  !online
                    ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20'
                    : 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20 hover:bg-[#F59E0B] hover:text-white'
                } ${syncing ? 'animate-pulse' : ''}`}
                title={!online ? 'Sin conexión' : `${pending} pendiente(s) por sincronizar`}
              >
                {!online ? <WifiOff className="w-3.5 h-3.5" /> : (syncing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />)}
                <span className="hidden sm:inline">{!online ? 'Offline' : `${pending} pend`}</span>
              </button>
            )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="user-menu-trigger"
                variant="ghost"
                className="flex items-center gap-2 hover:bg-[#FAFAFA] rounded-md"
              >
                <div className="w-8 h-8 rounded-md bg-[#0052FF]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#0052FF]" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold text-[#0A0A0A] leading-none">{user?.name}</p>
                  <p className="text-xs text-[#52525B] mt-0.5">{roleLabels[user?.role]}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-[#52525B] hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={async () => { await logout(); navigate('/login'); }}
                className="text-[#EF4444] cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <div className="w-72 h-full bg-white p-6 space-y-2 safe-top" onClick={e => e.stopPropagation()}>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-4">Navegacion</p>
            {items.map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                    active ? 'bg-[#0052FF] text-white' : 'text-[#52525B] hover:bg-[#10B981] hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-28 lg:pb-8 safe-x">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E4E4E7] safe-bottom safe-x" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {(mobileBottomNav[user?.role] || mobileBottomNav.brigadista).map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`bottom-nav-${item.path.slice(1)}`}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[60px] py-1.5 rounded-lg transition-all duration-200 ${
                  active
                    ? 'text-[#0052FF]'
                    : 'text-[#A1A1AA] active:text-[#52525B]'
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px] font-semibold leading-none">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
